import React, { useRef, useEffect, useCallback, memo } from 'react';
import { Box, IconButton } from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';

import {
  MediaCard,
  MediaCardActions,
  MediaCardContactFrame,
  MediaCardContent,
  MediaCardEditIcon,
  MediaCardInlineAction,
  MediaCardInlineEdit,
  MediaCardInlineInput,
  MediaCardName,
  MediaCardPreviewFrame,
  MediaCardThumbnail,
} from 'src/UIKit';
import ChatFileIcon from 'src/components/ChatFileIcon/view';
import { FileDisplayLabel } from 'src/components/FileDisplayLabel';
import VcfDisplayFromUrl from 'src/components/VcfViewer/view';
import { CsvPreview } from 'src/components/CsvPreview';
import { useAssetsApi } from 'src/modules/assetLibrary/api/AssetsApi';
import { isDocumentType } from 'src/modules/assetLibrary/helpers';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { GENERIC_ERROR_MESSAGE } from 'src/constants/messages';
import {
  ensureFileExtension,
  getFileDisplayNameAndExtension,
  getFileNameFromUrl,
  triggerBlobDownload,
} from 'src/helpers/files';

export type AssetCardAsset = {
  id: string;
  name?: string;
  url: string;
  mimeType: string;
};

interface AssetCardProps {
  asset: AssetCardAsset;
  isEditing?: boolean;
  editingName?: string;
  failedImages?: Set<string>;
  isUpdating?: boolean;
  onEditStart?: (asset: AssetCardAsset) => void;
  onEditSave?: (assetId: string) => void;
  onEditCancel?: () => void;
  onEditNameChange?: (name: string) => void;
  onDelete?: (asset: AssetCardAsset) => void;
  onImageError?: (assetId: string) => void;
  onPreview?: (asset: AssetCardAsset) => void;
  enableNameEdit?: boolean;
  showDownloadAction?: boolean;
  showDeleteAction?: boolean;
  previewOnClick?: boolean;
  previewOnlyOnSupportedTypes?: boolean;
  hideName?: boolean;
}

export const AssetCardComponent: React.FC<AssetCardProps> = ({
  asset,
  isEditing = false,
  editingName = '',
  failedImages = new Set<string>(),
  isUpdating = false,
  onEditStart,
  onEditSave,
  onEditCancel,
  onEditNameChange,
  onDelete,
  onImageError,
  onPreview,
  enableNameEdit = true,
  showDownloadAction = true,
  showDeleteAction = true,
  previewOnClick = true,
  previewOnlyOnSupportedTypes = true,
  hideName = false,
}) => {
  const editContainerRef = useRef<HTMLDivElement>(null);
  const { getFileContent } = useAssetsApi();
  const { name: displayName, extension: extensionFromMimeType } = getFileDisplayNameAndExtension(asset.name, asset.mimeType);

  const downloadFile = useCallback(async (fileUrl: string, filename: string = 'file') => {
    try {
      const response = await getFileContent(fileUrl);
      triggerBlobDownload(new Blob([response.data]), filename);
    } catch (error) {
      reportErrorToUser(error, GENERIC_ERROR_MESSAGE);
    }
  }, [getFileContent]);

  const handleDownload = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const filename = ensureFileExtension(
      asset.name || getFileNameFromUrl(asset.url) || 'asset',
      asset.mimeType,
    );
    downloadFile(asset.url, filename);
  }, [asset.name, asset.url, asset.mimeType, downloadFile]);

  const handleAssetClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const canPreview =
      asset.mimeType.startsWith('image/') ||
      asset.mimeType.startsWith('video/') ||
      asset.mimeType.startsWith('audio/') ||
      isDocumentType(asset.mimeType) ||
      asset.mimeType === 'text/vcard' ||
      asset.mimeType === 'text/x-vcard' ||
      asset.mimeType === 'text/calendar';

    if (!previewOnClick) {
      return;
    }

    if (onPreview && (!previewOnlyOnSupportedTypes || canPreview)) {
      onPreview(asset);
      return;
    }

    if (previewOnlyOnSupportedTypes) {
      handleDownload(e);
    }
  }, [asset, isDocumentType, onPreview, handleDownload]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditing && editContainerRef.current) {
        if (!editContainerRef.current.contains(event.target as Node)) {
          onEditCancel?.();
        }
      }
    };

    if (isEditing && onEditCancel) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isEditing, onEditCancel]);

  return (
    <MediaCard cardHeight={hideName ? 170 : 200}>
      <MediaCardContent>
        <MediaCardThumbnail onClick={handleAssetClick}>
          {asset.mimeType.startsWith('image/') && !failedImages.has(asset.id) ? (
            <Box
              component="img"
              src={asset.url}
              alt={asset.name || 'Asset'}
              borderRadius={1}
              style={{
                maxWidth: '100%',
                maxHeight: '120px',
                objectFit: 'contain',
              }}
              onError={() => onImageError?.(asset.id)}
            />
          ) : asset.mimeType === 'text/vcard' || asset.mimeType === 'text/x-vcard' ? (
            <MediaCardContactFrame>
              <VcfDisplayFromUrl vcfUrl={asset.url} />
            </MediaCardContactFrame>
          ) : isDocumentType(asset.mimeType) ? (
            <MediaCardPreviewFrame
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {asset.mimeType === 'application/pdf' ? (
                <iframe
                  src={`${asset.url}#toolbar=0&navpanes=0&scrollbar=0&page=1&zoom=fit`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    pointerEvents: 'none',
                  }}
                  title={`PDF Thumbnail: ${asset.name}`}
                  onError={() => {
                    console.warn('PDF preview failed for:', asset.url);
                  }}
                />
              ) : asset.mimeType === 'text/csv' ? (
                <Box width="100%" height="100%" style={{ pointerEvents: 'none' }}>
                  <CsvPreview csvUrl={asset.url} maxRows={5} />
                </Box>
              ) : asset.mimeType.startsWith('text/') || 
                     asset.mimeType === 'application/json' || 
                     asset.mimeType === 'application/xml' ? (
                <iframe
                  src={asset.url}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    pointerEvents: 'none',
                  }}
                  title={`Document Thumbnail: ${asset.name}`}
                  onError={() => {
                    console.warn('Text preview failed for:', asset.url);
                  }}
                />
              ) : (
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(asset.url)}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    pointerEvents: 'none',
                  }}
                  title={`Document Thumbnail: ${asset.name}`}
                  onError={() => {
                    console.warn('Office document preview failed, falling back to Google Docs viewer');
                  }}
                />
              )}
            </MediaCardPreviewFrame>
          ) : (
            <ChatFileIcon url={asset.url} mimeType={asset.mimeType} />
          )}
        </MediaCardThumbnail>

        {!hideName && (
        <Box flexGrow={1}>
          <MediaCardName
            nameGap={12}
            editable={enableNameEdit}
            clickable={enableNameEdit}
            untitled={!asset.name || asset.name === 'Untitled'}
          >
            {isEditing && enableNameEdit ? (
              <MediaCardInlineEdit ref={editContainerRef}>
                <MediaCardInlineInput
                  value={editingName}
                  onChange={e => onEditNameChange?.(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                      onEditSave?.(asset.id);
                    }
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      onEditCancel?.();
                    }
                  }}
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  disabled={isUpdating}
                  autoFocus
                />
                <MediaCardInlineAction
                  size="small"
                  color="success"
                  onClick={e => {
                    e.stopPropagation();
                    onEditSave?.(asset.id);
                  }}
                  disabled={isUpdating}
                >
                  <CheckIcon fontSize="small" />
                </MediaCardInlineAction>
                <MediaCardInlineAction
                  size="small"
                  color="error"
                  onClick={e => {
                    e.stopPropagation();
                    onEditCancel?.();
                  }}
                >
                  <CloseIcon fontSize="small" />
                </MediaCardInlineAction>
              </MediaCardInlineEdit>
            ) : (
              <>
                <FileDisplayLabel
                  name={displayName}
                  extension={extensionFromMimeType}
                  sx={{ px: 2.5 }}
                  onClick={() => {
                    if (enableNameEdit) {
                      onEditStart?.(asset);
                    }
                  }}
                />
                {enableNameEdit && (
                  <MediaCardEditIcon className="edit-icon" onClick={() => onEditStart?.(asset)}>
                    <EditIcon />
                  </MediaCardEditIcon>
                )}
              </>
            )}
          </MediaCardName>
        </Box>
        )}
      </MediaCardContent>

      {(showDownloadAction || (showDeleteAction && !!onDelete)) && (
        <MediaCardActions className="asset-actions" zIndex={9999}>
          {showDownloadAction && (
            <IconButton size="small" onClick={handleDownload} color="primary" sx={{ mr: 0.5 }}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          )}
          {showDeleteAction && onDelete && (
            <IconButton size="small" onClick={() => onDelete(asset)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </MediaCardActions>
      )}
    </MediaCard>
  );
};

export const AssetCard = memo(AssetCardComponent);
